import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, TrendingUp, TrendingDown } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function QuarterlyBusiness({ year, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/snapbudget/quarterly?year=${year}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [year, refreshKey]);

  if (!data) return null;
  const quarters = data.quarters || [];
  const cur = quarters[(data.current_quarter || 1) - 1] || quarters[0];
  const prev = quarters[(data.current_quarter || 1) - 2] || null;
  const delta = prev ? cur.net - prev.net : null;
  const maxVal = Math.max(1, ...quarters.map((q) => Math.max(q.income, q.expenses)));

  return (
    <section data-testid="snap-quarterly" className="bg-[#171208] border border-[#FFC43D]/30 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-amber-950/40">
      <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-white flex items-center gap-2">
            <span className="w-8 h-8 grid place-items-center rounded-lg bg-[#FFC43D]/15 border border-[#FFC43D]/30"><BarChart3 className="w-4 h-4 text-[#FFC43D]" /></span>
            Quarterly Business P&L
          </h2>
          <p className="text-xs text-slate-500 mt-1">{data.year} · income (incl. paid invoices) vs business expenses</p>
        </div>
      </div>

      {/* Current vs last quarter */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <div data-testid="snap-q-current" className="rounded-xl bg-slate-900/60 border border-[#FFC43D]/20 p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#FFC43D]">This quarter · {cur?.label}</p>
          <p className={`font-mono text-2xl font-bold mt-1 ${cur?.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(cur?.net)}</p>
          <div className="flex gap-4 mt-2 text-[11px] text-slate-500">
            <span>Income <span className="font-mono text-[#FFC43D]">{money(cur?.income)}</span></span>
            <span>Expenses <span className="font-mono text-rose-400">{money(cur?.expenses)}</span></span>
          </div>
        </div>
        <div data-testid="snap-q-prev" className="rounded-xl bg-slate-900/60 border border-white/10 p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Last quarter · {prev?.label || "—"}</p>
          <p className="font-mono text-2xl font-bold mt-1 text-slate-200">{prev ? money(prev.net) : "—"}</p>
          {delta !== null && (
            <div className={`flex items-center gap-1 mt-2 text-[11px] ${delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {delta >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {delta >= 0 ? "Up" : "Down"} {money(Math.abs(delta))} vs this quarter
            </div>
          )}
        </div>
      </div>

      {/* 4-quarter bars */}
      <div className="grid grid-cols-4 gap-3">
        {quarters.map((q) => {
          const incH = Math.round((q.income / maxVal) * 100);
          const exH = Math.round((q.expenses / maxVal) * 100);
          return (
            <div key={q.quarter} data-testid={`snap-q-bar-${q.quarter}`} className="flex flex-col items-center">
              <div className="flex items-end gap-1.5 h-28 w-full justify-center">
                <div className="w-1/3 rounded-t bg-[#FFC43D]/80" style={{ height: `${Math.max(2, incH)}%` }} title={`Income ${money(q.income)}`} />
                <div className="w-1/3 rounded-t bg-rose-500/70" style={{ height: `${Math.max(2, exH)}%` }} title={`Expenses ${money(q.expenses)}`} />
              </div>
              <div className="mt-2 text-center">
                <div className="text-[11px] font-semibold text-slate-300">{q.label}</div>
                <div className={`text-[11px] font-mono ${q.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(q.net)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-5 mt-4 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#FFC43D]/80" /> Income</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-rose-500/70" /> Expenses</span>
      </div>
    </section>
  );
}
